import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowRight, FiUsers } from 'react-icons/fi';
import { formatMoney } from '../utils/money';
import { scoreBadgeClasses, scoreTone } from '../utils/collectionScore';
import { RANKING_SORT_OPTIONS, displayMargen, sortClientRanking } from '../utils/clientRanking';

function initials(name = '') {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return parts.slice(0, 2).map((p) => p[0]).join('').toUpperCase();
}

export default function CobranzaClientRanking({
  clients = [],
  modoOscuro,
  box,
  divide,
  textPri,
  textMuted,
  onSelectClient,
}) {
  const [sortBy, setSortBy] = useState('pendiente');
  const sorted = useMemo(() => sortClientRanking(clients, sortBy), [clients, sortBy]);
  const green = modoOscuro ? 'text-green-300' : 'text-green-700';
  const red = modoOscuro ? 'text-red-300' : 'text-red-700';

  if (!clients.length) return null;

  return (
    <section className={`rounded-2xl border overflow-hidden ${box}`}>
      <div className={`px-4 py-3 border-b space-y-3 ${divide}`}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <div className="flex items-center gap-2">
              <FiUsers className={`w-4 h-4 ${textMuted}`} />
              <h2 className={`text-sm font-semibold ${textPri}`}>Ranking de clientes</h2>
            </div>
            <p className={`text-xs mt-0.5 ${textMuted}`}>
              Pendiente, score de cobranza y margen para priorizar a quién cobrar.
            </p>
          </div>
          <Link
            to="/clientes-detalle"
            className="text-xs font-semibold text-[#1F80FF] hover:underline inline-flex items-center gap-1"
          >
            Ver completo
            <FiArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {RANKING_SORT_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setSortBy(opt.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                sortBy === opt.id
                  ? modoOscuro ? 'border-[#1F80FF] bg-[#1F80FF]/20 text-[#5CA3FF]' : 'border-[#1F80FF] bg-[#1F80FF]/10 text-[#004AAD]'
                  : modoOscuro ? 'border-gray-700 text-gray-400 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`lg:hidden divide-y ${divide}`}>
        {sorted.map((row, index) => {
          const margen = displayMargen(row);
          return (
            <button
              key={row.cliente}
              type="button"
              onClick={() => onSelectClient?.(row.cliente)}
              className={`w-full px-4 py-3 text-left ${modoOscuro ? 'hover:bg-gray-800/50' : 'hover:bg-gray-50'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    modoOscuro ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold truncate ${textPri}`}>{row.cliente}</div>
                    <div className={`text-xs ${textMuted}`}>{row.impagas} impaga(s)</div>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`text-sm font-bold tabular-nums ${textPri}`}>
                    {formatMoney(row.pendiente, 'ARS')}
                  </div>
                  {row.score != null && (
                    <span className={`inline-flex mt-1 px-2 py-0.5 rounded-lg text-[10px] font-bold ${scoreBadgeClasses(scoreTone(row.score), modoOscuro)}`}>
                      {row.score}
                    </span>
                  )}
                </div>
              </div>
              {margen && (
                <div className={`text-xs mt-2 ${margen.value >= 0 ? green : red}`}>
                  Margen {margen.label}: {formatMoney(margen.value, 'ARS')}
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="hidden lg:block overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className={modoOscuro ? 'bg-gray-800/80 text-gray-400' : 'bg-gray-50 text-gray-500'}>
            <tr>
              <th className="py-2.5 pl-4 pr-2 font-semibold text-left w-10">#</th>
              <th className="py-2.5 px-2 font-semibold text-left">Cliente</th>
              <th className="py-2.5 px-2 font-semibold text-right">Pendiente</th>
              <th className="py-2.5 px-2 font-semibold text-center">Score</th>
              <th className="py-2.5 pr-4 pl-2 font-semibold text-right">Margen</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${divide}`}>
            {sorted.map((row, index) => {
              const margen = displayMargen(row);
              return (
                <tr
                  key={row.cliente}
                  onClick={() => onSelectClient?.(row.cliente)}
                  className={`cursor-pointer ${modoOscuro ? 'hover:bg-gray-800/40' : 'hover:bg-gray-50/80'}`}
                >
                  <td className={`py-3 pl-4 pr-2 text-xs font-semibold ${textMuted}`}>{index + 1}</td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-8 h-8 rounded-xl flex items-center justify-center text-[10px] font-semibold shrink-0 ${
                        modoOscuro ? 'bg-[#1F80FF]/20 text-[#5CA3FF]' : 'bg-[#1F80FF]/10 text-[#1F80FF]'
                      }`}>
                        {initials(row.cliente)}
                      </div>
                      <div className="min-w-0">
                        <div className={`font-medium truncate ${textPri}`}>{row.cliente}</div>
                        <div className={`text-xs ${textMuted}`}>{row.impagas} impaga(s)</div>
                      </div>
                    </div>
                  </td>
                  <td className={`py-3 px-2 text-right font-bold tabular-nums ${textPri}`}>
                    {formatMoney(row.pendiente, 'ARS')}
                  </td>
                  <td className="py-3 px-2 text-center">
                    {row.score != null ? (
                      <span className={`inline-flex flex-col items-center px-2 py-1 rounded-lg text-[10px] font-bold ${scoreBadgeClasses(scoreTone(row.score), modoOscuro)}`}>
                        <span>{row.score}</span>
                        {row.scoreLabel && <span className="font-medium opacity-80">{row.scoreLabel}</span>}
                      </span>
                    ) : (
                      <span className={textMuted}>—</span>
                    )}
                  </td>
                  <td className="py-3 pr-4 pl-2 text-right">
                    {margen ? (
                      <div>
                        <div className={`font-semibold tabular-nums ${margen.value >= 0 ? green : red}`}>
                          {formatMoney(margen.value, 'ARS')}
                        </div>
                        <div className={`text-[10px] uppercase tracking-wide ${textMuted}`}>{margen.label}</div>
                      </div>
                    ) : (
                      <span className={textMuted}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
